import { weightedPurchasePrices } from './purchaseLines';
import PermissionButton from '../../permissions/PermissionButton';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Space, Typography, notification } from 'antd';
import {
  DataGrid,
  gridClasses,
  useGridApiRef
} from '@mui/x-data-grid';
import { Box, Button, CircularProgress } from '@mui/material';
import { bulkUpdatePrices, getArticleById } from '../../models/articleAPI';

const { Text } = Typography;

const PAGE_SIZE = 100;

const PriceUpdateModal = ({
  open,
  onClose,
  items = [],
  onApplied,
  title = "Mise à jour des prix d'articles",
  /** When true: show old PA (base article) vs new PA (document), no editable PA/PV */
  compareWithArticle = false,
}) => {
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState([]);
  const [selection, setSelection] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);

  const safeNumber = (v) => {
    if (v === undefined || v === null || v === '' || v === -1) return 0;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  const numberParser = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsedValue = parseFloat(value.toString().replace(',', '.'));
    return isNaN(parsedValue) ? 0 : parsedValue;
  };

  const round2 = (value) => {
    const n = Number(value);
    if (isNaN(n)) return 0;
    return Number(n.toFixed(2));
  };

  const formatNum = (v) => {
    const n = Number(v);
    return isNaN(n) ? '' : n.toFixed(2);
  };

  // Initialize rows from provided items
  useEffect(() => {
    if (!open) return;

    if (compareWithArticle) {
      const initial = weightedPurchasePrices(items)
        .filter(r => r && r.CODE_ART)
        .map(r => {
          const art = r.article || {};
          const hasOldPa = art.PA_HT !== undefined && art.PA_HT !== null;
          return {
            id: r.CODE_ART,
            CODE_ART: r.CODE_ART,
            ARTICLE: r.ARTICLE || art.ARTICLE,
            QTE: r.QTE,
            OLD_PA_HT: hasOldPa ? safeNumber(art.PA_HT) : null,
            OLD_PA_TTC: art.PA_TTC !== undefined && art.PA_TTC !== null ? safeNumber(art.PA_TTC) : null,
            // Bon de réception / document prices
            PA_HT: safeNumber(r.PA_HT),
            PA_TTC: safeNumber(r.PA_TTC),
          };
        });
      setRows(initial);
      setSelection(initial.map(r => r.id));

      const codesNeedingOldPa = initial
        .filter(r => r.OLD_PA_HT === null || r.OLD_PA_TTC === null)
        .map(r => r.CODE_ART);

      if (codesNeedingOldPa.length > 0) {
        setLoadingPrices(true);
        (async () => {
          try {
            const updates = {};
            for (const code of codesNeedingOldPa) {
              try {
                const res = await getArticleById(code);
                const data = res?.data || res;
                if (data) {
                  updates[code] = {
                    OLD_PA_HT: safeNumber(data.PA_HT),
                    OLD_PA_TTC: safeNumber(data.PA_TTC),
                  };
                }
              } catch (_) {
                // ignore fetch errors per item
              }
            }
            if (Object.keys(updates).length > 0) {
              setRows(prev => prev.map(r => updates[r.CODE_ART] ? { ...r, ...updates[r.CODE_ART] } : r));
            }
          } finally {
            setLoadingPrices(false);
          }
        })();
      }
      return;
    }

    const initial = weightedPurchasePrices(items)
      .filter(r => r && r.CODE_ART)
      .map(r => ({
        id: r.CODE_ART,
        CODE_ART: r.CODE_ART,
        ARTICLE: r.ARTICLE,
        QTE: r.QTE,
        PA_HT: safeNumber(r.PA_HT),
        PA_TTC: safeNumber(r.PA_TTC),
        PV_HT: r.PV_HT === undefined || r.PV_HT === null ? null : safeNumber(r.PV_HT),
        PV_TTC: r.PV_TTC === undefined || r.PV_TTC === null ? null : safeNumber(r.PV_TTC),
        TVA: safeNumber(r.TVA),
      }));
    setRows(initial);
    setSelection(initial.map(r => r.id)); // preselect all by default

    // Fetch missing PV fields from article when not present
    const codesNeedingPv = initial
      .filter(r => (r.PV_HT === null || r.PV_TTC === null || isNaN(r.PV_HT) || isNaN(r.PV_TTC) || !r.TVA))
      .map(r => r.CODE_ART);

    if (codesNeedingPv.length > 0) {
      setLoadingPrices(true);
      (async () => {
        try {
          const updates = {};
          for (const code of codesNeedingPv) {
            try {
              const res = await getArticleById(code);
              const data = res?.data || res;
              if (data) {
                updates[code] = {
                  PV_HT: data.PV_HT === undefined || data.PV_HT === null ? null : safeNumber(data.PV_HT),
                  PV_TTC: data.PV_TTC === undefined || data.PV_TTC === null ? null : safeNumber(data.PV_TTC),
                  TVA: safeNumber(data.TVA)
                };
              }
            } catch (_) {
              // ignore fetch errors per item
            }
          }
          if (Object.keys(updates).length > 0) {
            setRows(prev => prev.map(r => updates[r.CODE_ART] ? { ...r, ...updates[r.CODE_ART] } : r));
          }
        } finally {
          setLoadingPrices(false);
        }
      })();
    }
  }, [open, items, compareWithArticle]);

  const columns = useMemo(() => {
    if (compareWithArticle) {
      return [
        { field: 'CODE_ART', headerName: 'Code', width: 130 },
        { field: 'QTE', headerName: 'Qté totale', width: 100, type: 'number' },
        { field: 'ARTICLE', headerName: 'Article', flex: 1, minWidth: 200 },
        {
          field: 'OLD_PA_HT',
          headerName: 'PA HT (A)',
          width: 120,
          type: 'number',
          editable: false,
          renderCell: (params) => formatNum(params.value),
        },
        {
          field: 'OLD_PA_TTC',
          headerName: 'PA TTC (A)',
          width: 120,
          type: 'number',
          editable: false,
          renderCell: (params) => formatNum(params.value),
        },
        {
          field: 'PA_HT',
          headerName: 'PA HT pondéré',
          width: 120,
          type: 'number',
          editable: false,
          renderCell: (params) => formatNum(params.value),
        },
        {
          field: 'PA_TTC',
          headerName: 'PA TTC pondéré',
          width: 120,
          type: 'number',
          editable: false,
          renderCell: (params) => formatNum(params.value),
        },
      ];
    }

    return [
      { field: 'CODE_ART', headerName: 'Code', width: 130 },
        { field: 'QTE', headerName: 'Qté totale', width: 100, type: 'number' },
      { field: 'ARTICLE', headerName: 'Article', flex: 1, minWidth: 220 },
      {
        field: 'PA_HT',
        headerName: 'PA HT',
        width: 110,
        type: 'number',
        editable: true,
        renderCell: (params) => formatNum(params.value),
        valueParser: (value) => numberParser(value)
      },
      {
        field: 'PA_TTC',
        headerName: 'PA TTC',
        width: 110,
        type: 'number',
        editable: true,
        renderCell: (params) => formatNum(params.value),
        valueParser: (value) => numberParser(value)
      },
      {
        field: 'PV_HT',
        headerName: 'PV HT',
        width: 110,
        type: 'number',
        editable: true,
        renderCell: (params) => formatNum(params.value),
        valueParser: (value) => numberParser(value)
      },
      {
        field: 'PV_TTC',
        headerName: 'PV TTC',
        width: 110,
        type: 'number',
        editable: true,
        renderCell: (params) => formatNum(params.value),
        valueParser: (value) => numberParser(value)
      },
      {
        field: 'TVA',
        headerName: 'TVA%',
        width: 90,
        type: 'number',
        editable: true,
        renderCell: (params) => formatNum(params.value),
        valueParser: (value) => numberParser(value)
      },
    ];
  }, [compareWithArticle]);

  const processRowUpdate = (newRow, oldRow) => {
    if (compareWithArticle) return oldRow;

    const numericFields = ['PA_HT', 'PA_TTC', 'PV_HT', 'PV_TTC', 'TVA'];
    const processed = { ...newRow };
    numericFields.forEach(f => {
      if (f in processed) {
        processed[f] = numberParser(processed[f]);
      }
    });
    const tva = numberParser(processed.TVA || oldRow.TVA || 0);
    const changedField = Object.keys(processed).find(k => processed[k] !== oldRow[k]);
    if (changedField === 'PA_HT') {
      processed.PA_TTC = round2(processed.PA_HT * (1 + tva / 100));
    } else if (changedField === 'PA_TTC') {
      processed.PA_HT = tva ? round2(processed.PA_TTC / (1 + tva / 100)) : processed.PA_TTC;
    } else if (changedField === 'PV_HT') {
      processed.PV_TTC = processed.PV_HT === null ? null : round2(processed.PV_HT * (1 + tva / 100));
    } else if (changedField === 'PV_TTC') {
      processed.PV_HT = processed.PV_TTC === null ? null : (tva ? round2(processed.PV_TTC / (1 + tva / 100)) : processed.PV_TTC);
    } else if (changedField === 'TVA') {
      if (processed.PA_HT !== undefined) processed.PA_TTC = round2(processed.PA_HT * (1 + tva / 100));
      if (processed.PV_HT !== null && processed.PV_HT !== undefined) processed.PV_TTC = round2(processed.PV_HT * (1 + tva / 100));
    }
    setRows(prev => prev.map(r => r.id === processed.id ? processed : r));
    return processed;
  };

  const handleApply = async () => {
    if (!selection || selection.length === 0) {
      notification.warning({ message: 'Sélection vide', description: 'Sélectionnez au moins un article.' });
      return;
    }
    setLoading(true);
    try {
      const payload = rows
        .filter(r => selection.includes(r.id))
        .map(r => {
          if (compareWithArticle) {
            // Apply bon de réception PA to base article only (no PV change)
            return {
              CODE_ART: r.CODE_ART,
              PA_HT: numberParser(r.PA_HT),
              PA_TTC: numberParser(r.PA_TTC),
            };
          }
          return {
            CODE_ART: r.CODE_ART,
            PA_HT: numberParser(r.PA_HT),
            PA_TTC: numberParser(r.PA_TTC),
            ...(r.PV_HT === null ? {} : { PV_HT: numberParser(r.PV_HT) }),
            ...(r.PV_TTC === null ? {} : { PV_TTC: numberParser(r.PV_TTC) }),
          };
        });

      const result = await bulkUpdatePrices(payload);

      if (result.successCount > 0) {
        notification.success({
          message: 'Prix mis à jour',
          description: `${result.successCount} article(s) mis à jour sur ${result.total}`,
          placement: 'bottomLeft'
        });
      }
      if (result.failCount > 0) {
        notification.warning({
          message: 'Mises à jour partielles',
          description: `${result.failCount} article(s) non mis à jour`,
          placement: 'bottomLeft'
        });
      }
      if (onApplied) onApplied(result);
      onClose();
    } catch (e) {
      notification.error({ message: 'Erreur', description: e?.message || 'Échec de la mise à jour' });
    } finally {
      setLoading(false);
    }
  };

  const helpText = compareWithArticle
    ? 'Prix pondéré = somme(Qté × PA) / somme(Qté), uniquement sur les lignes de ce bon, sans le stock existant. Sélectionnez les articles puis Appliquer pour mettre à jour leur PA.'
    : 'Les PA proposés sont pondérés par les quantités de ce document. Modifiez les PA/PV si nécessaire, sélectionnez les articles et cliquez sur Appliquer.';

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={compareWithArticle ? 900 : 980}
      footer={[
        <Space key="actions">
          <Button onClick={onClose} disabled={loading}>Annuler</Button>
          <PermissionButton permissionModule="articles" permissionAction="prices" permissionLibrary="mui" variant="contained" color="primary" onClick={handleApply} disabled={loading || loadingPrices}>
            Appliquer la mise à jour
          </PermissionButton>
        </Space>
      ]}
    >
      <Box sx={{ mb: 1 }}>
        <Text type="secondary">{helpText}</Text>
      </Box>
      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={() => {}}
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          editMode={compareWithArticle ? undefined : 'cell'}
          onCellEditCommit={compareWithArticle ? undefined : (params) => {
            const { id, field, value } = params;
            setRows(prev => prev.map(r => {
              if (r.id !== id) return r;
              const updated = { ...r, [field]: numberParser(value) };
              const tva = numberParser(updated.TVA || 0);
              if (field === 'PA_HT') updated.PA_TTC = round2(updated.PA_HT * (1 + tva / 100));
              if (field === 'PA_TTC') updated.PA_HT = tva ? round2(updated.PA_TTC / (1 + tva / 100)) : updated.PA_TTC;
              if (field === 'PV_HT') updated.PV_TTC = updated.PV_HT === null ? null : round2(updated.PV_HT * (1 + tva / 100));
              if (field === 'PV_TTC') updated.PV_HT = updated.PV_TTC === null ? null : (tva ? round2(updated.PV_TTC / (1 + tva / 100)) : updated.PV_TTC);
              if (field === 'TVA') {
                if (updated.PA_HT !== undefined) updated.PA_TTC = round2(updated.PA_HT * (1 + tva / 100));
                if (updated.PV_HT !== null && updated.PV_HT !== undefined) updated.PV_TTC = round2(updated.PV_HT * (1 + tva / 100));
              }
              return updated;
            }));
          }}
          pageSizeOptions={[PAGE_SIZE]}
          initialState={{
            pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } },
            sorting: { sortModel: [{ field: 'CODE_ART', sort: 'asc' }] }
          }}
          sx={{
            [`& .${gridClasses.cell}`]: { fontSize: '0.85rem', padding: '2px 8px' },
            '& .MuiDataGrid-footerContainer': { border: 'none' }
          }}
        />
      </Box>
      {loadingPrices && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
          <CircularProgress size={16} />
          <Text type="secondary">
            {compareWithArticle ? 'Chargement des anciens prix…' : 'Chargement des prix de vente…'}
          </Text>
        </Box>
      )}
    </Modal>
  );
};

export default PriceUpdateModal;
